import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../branding/BrandProvider';
import { supabase } from '../supabaseClient';
import '../components/Onboarding/style.css';
import './plan-selection.css';

const DEFAULT_PLANS = [
  {
    id: 'monthly',
    interval: 'monthly',
    name: 'Monthly Plan',
    price: '$149.99',
    intervalLabel: 'per month',
    trial: '7-day free trial',
    billingNote: 'Billed monthly after trial',
    tagline: 'Flexible month-to-month accountability.',
    description: 'Ideal if you want to start fast with a lower commitment.',
    features: [
      'Custom Workout Plan',
      'Custom Meal Plan',
      'Direct Coach Oversight',
      '24/7 AI Coach Support',
    ],
  },
  {
    id: 'yearly',
    interval: 'yearly',
    name: 'Annual Plan',
    price: '$1,499.99',
    intervalLabel: 'per year',
    badge: 'Most popular',
    trial: '7-day free trial',
    billingNote: 'Billed annually after trial',
    tagline: 'Best value for long-term results.',
    description: 'Lock in the best rate and build consistent progress.',
    features: [
      'Custom Workout Plan',
      'Custom Meal Plan',
      'Direct Coach Oversight',
      '24/7 AI Coach Support',
    ],
  },
];

export default function PlanSelectionPage() {
  const { name: brandName, logos } = useBrand();
  const brandLogo = logos?.fullLarge || logos?.full || logos?.fullWhite;
  const brandAlt = brandName || 'Logo';
  const navigate = useNavigate();
  const plans = useMemo(() => DEFAULT_PLANS, []);
  const defaultPlanId = plans.find((plan) => plan.id === 'yearly')?.id || plans[0]?.id || '';
  const [selectedId, setSelectedId] = useState(defaultPlanId);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedPlan = plans.find((plan) => plan.id === selectedId) || null;
  const hasSelection = Boolean(selectedPlan);

  const handleContinue = async () => {
    setError('');
    if (!selectedPlan) {
      setError('Select a package to continue.');
      return;
    }
    setLoading(true);
    try {
      const { data: auth } = await supabase.auth.getSession();
      const session = auth?.session || null;
      if (!session?.user?.id) {
        setError('Please sign in again to continue.');
        navigate('/login');
        return;
      }

      const rawApiBase = process.env.REACT_APP_API_BASE || '';
      const apiRoot = rawApiBase
        ? (rawApiBase.startsWith('http') ? rawApiBase : `https://${rawApiBase}`)
        : (typeof window !== 'undefined' ? window.location.origin : '');
      const apiBase = apiRoot.endsWith('/api')
        ? apiRoot
        : `${apiRoot.replace(/\/$/, '')}/api`;
      let checkoutUrl = '';
      try {
        checkoutUrl = new URL('stripe/create-checkout-session', `${apiBase.replace(/\/$/, '')}/`).toString();
      } catch (_) {
        throw new Error('Invalid API base URL. Check REACT_APP_API_BASE.');
      }
      const res = await fetch(checkoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: session.access_token ? `Bearer ${session.access_token}` : '',
        },
        body: JSON.stringify({
          planInterval: selectedPlan.interval,
          userId: session.user.id,
          email: session.user.email,
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload?.error || 'Unable to start checkout');
      }

      try {
        localStorage.setItem('selected_plan_id', selectedPlan.id);
        localStorage.setItem('selected_plan_name', selectedPlan.name);
      } catch (_) { }

      if (!payload?.url) {
        throw new Error('Checkout session did not return a redirect URL. Check your backend/Stripe config.');
      }
      window.location.href = payload.url;
    } catch (err) {
      setError(err?.message || 'Unable to start checkout.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="plan-selection-page">
      <div className="onboarding-container">
        <header className="plan-selection-header">
          {brandLogo && <img src={brandLogo} alt={brandAlt} className="plan-selection-logo" />}
          <div className="plan-selection-copy">
            <h1 className="plan-title">Choose your plan</h1>
            <p className="plan-subtitle">Start your 7-day free trial and we will begin your assessment.</p>
          </div>
        </header>

        <section className="plan-grid">
          {plans.map((plan) => {
            const isSelected = plan.id === selectedId;
            return (
              <button
                key={plan.id}
                type="button"
                className={`plan-card ${plan.badge ? 'featured' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedId(plan.id)}
              >
                {plan.badge && <div className="plan-badge">{plan.badge}</div>}
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price-row">
                  <div className="plan-price">{plan.price}</div>
                  <div className="plan-interval">{plan.intervalLabel}</div>
                  {plan.postPromoPrice && <div className="plan-price-strike">{plan.postPromoPrice}</div>}
                </div>
                <div className="plan-tagline">{plan.tagline}</div>
                <p className="plan-description">{plan.description}</p>
                {(plan.trial || plan.billingNote) && (
                  <div className="plan-promo">
                    {plan.trial && <span>{plan.trial}</span>}
                    {plan.billingNote && <span>{plan.billingNote}</span>}
                  </div>
                )}
                <ul className="plan-features">
                  {(plan.features || []).map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </section>

        {error && <div className="plan-error">{error}</div>}

        <div className="plan-actions">
          <button className="cta-btn--ghost" type="button" onClick={() => navigate('/login')}>
            Back to sign in
          </button>
          <button
            className={`cta-btn plan-continue ${hasSelection ? 'plan-continue--ready' : ''}`}
            type="button"
            onClick={handleContinue}
            disabled={loading || !hasSelection}
          >
            {loading ? 'Redirecting...' : 'Continue to payment'}
          </button>
        </div>

        <p className="plan-footnote">
          Apple Pay, Google Pay, ACH, Cash App Pay, Klarna, Afterpay, and cards are supported when enabled in Stripe.
        </p>
      </div>
    </main>
  );
}
